import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { CreditCard, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

// Used in two cases:
//   1. Admin set the user a temporary password and stamped
//      `must_change_password=true` → on next login we redirect here.
//   2. Voluntary password change from Settings.
// The page works the same in both cases — the user supplies current + new
// password. When `must_change_password` was true and the change succeeds, the
// flag is auto-cleared server-side so the redirect loop ends.
export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.changePassword(current, next);
      setDone(true);
      toast.success("Password changed");
      // Refresh auth context so must_change_password=false is reflected.
      if (checkAuth) await checkAuth();
      setTimeout(() => navigate("/"), 1200);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to change password");
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="change-password-done">
        <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8 text-center">
          <CheckCircle size={40} weight="duotone" className="text-emerald-500 mx-auto" />
          <h1 className="text-lg font-bold mt-3 text-zinc-900">Password updated</h1>
          <p className="text-sm text-zinc-600 mt-2">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="change-password-page">
      <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard size={20} weight="duotone" className="text-[#002FA7]" />
          <h1 className="text-xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "DM Sans, sans-serif" }}>Expenser</h1>
        </div>
        <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-bold mb-6">Change your password</p>

        {forced && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex gap-2" data-testid="force-change-banner">
            <Warning size={14} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-900 leading-snug">
              An admin set you a temporary password. Choose a new one to continue.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">{forced ? "Temporary password" : "Current password"}</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              className="mt-1 rounded-sm" data-testid="change-current" autoFocus required />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters"
              className="mt-1 rounded-sm" data-testid="change-new" required />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 rounded-sm" data-testid="change-confirm" required />
          </div>
          {error && <p className="text-xs text-red-500" data-testid="change-error">{error}</p>}
          <Button type="submit" disabled={submitting} data-testid="change-submit"
            className="w-full bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm">
            {submitting ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
